import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
// Desde el módulo sin imports, NO desde avivador-filtros: este hook va en el
// chunk principal (badge del menú) y no tiene que arrastrar la v2 (review T3a, H2).
import { decidirCongelado } from '@/components/avivador/avivador-congelado';
import type {
  ActualizarAvivadorConfigInput,
  ActualizarAvivadorConfigResponse,
  AvivadorBadge,
  AvivadorConfig,
  AvivadorConfigImpacto,
  AvivadorResponse,
  PosponerInput,
  PosponerResponse,
} from '@/types/api';

// Hooks de datos del Avivador (build 21/9/2026, context/design_avivador.md
// §10). Todo bajo la query key raíz ['avivador'] para que una sola
// invalidación (invalidarAvivador, o la de cada mutation existente que ya la
// suma — use-rdt-mutations.ts, use-estado-redes.ts) alcance a todo lo que
// esta pantalla mira.

const AVIVADOR_STALE_MS = 30_000;

/** Invalida TODO lo del Avivador (población + badge + config) — lo llaman
 *  las mutations propias de acá y, importado, cualquier mutation existente
 *  que pueda mover una rede de rama (chequeos, montos, hitos, estado de
 *  redes). */
export function invalidarAvivador(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['avivador'] });
}

/**
 * GET /api/avivador. Expone `data` CONGELADO a propósito (§9.3.8 de
 * design_avivador.md — «nunca se reordena la cola mientras la gestora
 * trabaja»): un refetch en segundo plano (volver el foco a la pestaña,
 * staleTime vencido, u otra pantalla invalidando ['avivador']) no cambia lo
 * que se ve hasta que el caller invoca `aplicarNovedades()`. El primer fetch
 * se aplica derecho, sin banner.
 *
 * `refetchOnMount: 'always'` y se congela SOLO una foto fresca, según el
 * reductor puro `decidirCongelado` (avivador-congelado.ts): la obtenida
 * después del montaje, o la de la caché si no está vencida ni invalidada.
 * Mientras tanto `data` es `undefined` (la página muestra el skeleton): al
 * volver de la rede después de registrar algo, la card resuelta ya no
 * aparece.
 *
 * T5: la Vidriera (v1) que congelaba el primer dato tal cual se archivó (era
 * el único otro caller); este hook queda con el único comportamiento que usa
 * `use-avivador-estado.ts` (plan, Ajuste #3).
 */
export function useAvivador() {
  const query = useQuery({
    queryKey: ['avivador'],
    queryFn: () => api<AvivadorResponse>('/api/avivador'),
    staleTime: AVIVADOR_STALE_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const [congelado, setCongelado] = useState<AvivadorResponse | undefined>(undefined);
  const huboPrimerFetch = useRef(false);

  // Congela solo una foto fresca. Lo que llega después de la primera foto va
  // al aviso de novedades (T3b), no a la pantalla.
  const { isFetchedAfterMount, isStale } = query;
  useEffect(() => {
    const congelar = decidirCongelado({
      tieneDatos: query.data !== undefined,
      isFetchedAfterMount,
      isStale,
      yaCongelado: huboPrimerFetch.current,
    });
    if (!congelar) return;
    huboPrimerFetch.current = true;
    setCongelado(query.data);
  }, [query.data, isFetchedAfterMount, isStale]);

  // SOLO la foto congelada (sin datos viejos de la caché mientras llega la fresca).
  const data = congelado;

  function aplicarNovedades(): void {
    if (query.data) setCongelado(query.data);
  }

  // `datosFrescos`: lo último que trajo la query, sin congelar — lo usa
  // `use-avivador-estado.ts` para lo que no mueve el mosaico (la lista de
  // pospuestas: cancelar una posposición tiene que sacarla del popover al
  // toque) y para contar las novedades por clave de entidad (`contarCambios`).
  return { ...query, data, datosFrescos: query.data, aplicarNovedades };
}

/** GET /api/avivador/badge — para el badge de la nav (`NavItem`), liviano:
 *  no hidrata items. Mismo staleTime que la población completa. */
export function useAvivadorBadge() {
  return useQuery({
    queryKey: ['avivador', 'badge'],
    queryFn: () => api<AvivadorBadge>('/api/avivador/badge'),
    staleTime: AVIVADOR_STALE_MS,
    refetchOnWindowFocus: true,
  });
}

/** GET /api/avivador/config — día D + los 6 umbrales vigentes. */
export function useAvivadorConfig() {
  return useQuery({
    queryKey: ['avivador', 'config'],
    queryFn: () => api<AvivadorConfig>('/api/avivador/config'),
    staleTime: AVIVADOR_STALE_MS,
  });
}

/** GET /api/avivador/config/impacto?... — aviso previo del form de
 *  configuración («con estos valores N redes entran y M salen»), ANTES de
 *  guardar. `params` son los mismos campos que ActualizarAvivadorConfigInput
 *  (sin `motivo`), como querystring — el form arma el objeto, este hook solo
 *  lo serializa y no cachea entre combinaciones distintas de valores.
 *  `enabled` en false mientras el form no tiene un candidato para simular. */
export function useAvivadorConfigImpacto(
  params: Omit<ActualizarAvivadorConfigInput, 'motivo'> | null,
) {
  const qs = params ? new URLSearchParams(paramsAQuery(params)).toString() : '';
  return useQuery({
    queryKey: ['avivador', 'config', 'impacto', qs],
    queryFn: () => api<AvivadorConfigImpacto>(`/api/avivador/config/impacto?${qs}`),
    enabled: params !== null,
    staleTime: 0,
  });
}

function paramsAQuery(input: Omit<ActualizarAvivadorConfigInput, 'motivo'>): Record<string, string> {
  const out: Record<string, string> = {};
  if (input.diaD !== undefined) out.diaD = String(input.diaD);
  // Hallazgo 9 (review v3): sin el prefijo "u" — tienen que calzar tal cual
  // con los nombres de CAMPOS_UMBRAL en backend/src/routes/avivador.ts
  // (~línea 1469: 'entregarProv', 'entregarDef', 'aprobarProv', 'aprobarDef',
  // 'facturarProv', 'facturarDef'). Con el prefijo "u" el backend nunca los
  // reconocía y GET /config/impacto devolvía siempre "sin cambios" para los
  // 6 umbrales, aunque el PUT real sí los aplicaba (ese sí valida por body).
  if (input.umbrales?.entregar?.Provisoria !== undefined) out.entregarProv = String(input.umbrales.entregar.Provisoria);
  if (input.umbrales?.entregar?.Definitiva !== undefined) out.entregarDef = String(input.umbrales.entregar.Definitiva);
  if (input.umbrales?.aprobar?.Provisoria !== undefined) out.aprobarProv = String(input.umbrales.aprobar.Provisoria);
  if (input.umbrales?.aprobar?.Definitiva !== undefined) out.aprobarDef = String(input.umbrales.aprobar.Definitiva);
  if (input.umbrales?.facturar?.Provisoria !== undefined) out.facturarProv = String(input.umbrales.facturar.Provisoria);
  if (input.umbrales?.facturar?.Definitiva !== undefined) out.facturarDef = String(input.umbrales.facturar.Definitiva);
  // Card v3 (§12.3/§12.5): el reparto vivas/olvidadas del impacto solo viaja
  // si el candidato incluye `inactividadDias`.
  if (input.inactividadDias !== undefined) out.inactividadDias = String(input.inactividadDias);
  return out;
}

/** POST /api/avivador/posponer — fecha+nota obligatorias (§2 punto 8). */
export function usePosponer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PosponerInput) => api<PosponerResponse>('/api/avivador/posponer', { method: 'POST', body: input }),
    onSuccess: () => invalidarAvivador(queryClient),
  });
}

/** POST /api/avivador/posponer/:id/cancelar — el «Deshacer» de posponer
 *  (evento compensatorio, full forense). */
export function useCancelarPosposicion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<{ ok: true }>(`/api/avivador/posponer/${id}/cancelar`, { method: 'POST' }),
    onSuccess: () => invalidarAvivador(queryClient),
  });
}

/** PUT /api/avivador/config (gestor+) — auditoría full forense
 *  (antes/después/motivo); devuelve la config nueva + el impacto ya
 *  confirmado. */
export function useActualizarConfigAvivador() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ActualizarAvivadorConfigInput) =>
      api<ActualizarAvivadorConfigResponse>('/api/avivador/config', { method: 'PUT', body: input }),
    // La config nueva entra al cache YA (review T2, hallazgo 3): sin esto, el
    // form de Gestión compara sus valores nuevos contra la config vieja hasta
    // que vuelve el refetch, y mientras tanto muestra «hay cambios», el
    // Motivo vacío y «Guardar» habilitado. La invalidación sigue: la
    // población y el badge dependen de la config.
    onSuccess: res => {
      queryClient.setQueryData<AvivadorConfig>(['avivador', 'config'], res.config);
      invalidarAvivador(queryClient);
    },
  });
}
